// src/components/Camera.js
import React, { useState, useRef } from 'react';

export default function Camera({ 
  onPhotoCapture, 
  label = "📸 Capture Photo" 
}) {
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const photoData = {
        dataUrl: event.target.result,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        timestamp: new Date().toISOString()
      };
      
      setPhoto(photoData);
      
      if (onPhotoCapture) {
        onPhotoCapture(photoData);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhoto(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onPhotoCapture) {
      onPhotoCapture(null);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div style={{
      background: '#0f2419',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #2d5a45'
    }}>
      <label style={{ 
        display: 'block', 
        color: '#4ade80', 
        marginBottom: '12px',
        fontWeight: 'bold'
      }}>
        {label}
      </label>

      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '10px',
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {photo ? (
        <div>
          <div style={{
            background: '#1a3327',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '12px'
          }}>
            <div style={{ color: '#4ade80', marginBottom: '8px', fontWeight: 'bold' }}>
              ✓ Photo Captured
            </div>
            
            <img 
              src={photo.dataUrl} 
              alt="Captured"
              style={{
                width: '100%',
                maxHeight: '200px',
                objectFit: 'cover',
                borderRadius: '4px',
                marginBottom: '8px'
              }}
            />
            
            <div style={{ color: '#888', fontSize: '12px' }}>
              <div>{photo.fileName}</div>
              <div>{(photo.fileSize / 1024).toFixed(1)} KB</div>
              <div>{new Date(photo.timestamp).toLocaleString()}</div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={triggerFileSelect}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              📸 Retake
            </button>
            <button
              type="button"
              onClick={clearPhoto}
              style={{
                padding: '8px 16px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              ✕ Clear
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={triggerFileSelect}
          style={{
            padding: '12px 24px',
            background: '#4ade80',
            color: '#0f2419',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            width: '100%'
          }}
        >
          📸 Take Photo or Select Image
        </button>
      )}

      <p style={{ 
        color: '#666', 
        fontSize: '11px', 
        margin: '12px 0 0 0' 
      }}>
        Photos help document soil samples and farm activities for verification.
      </p>
    </div>
  );
}